import { SyncOutlined } from '@ant-design/icons';
import { faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Empty, Input, Table } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import queryString from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import colors from '../../foundation/assets/colors';
import LogoPoweredByBiologis from '../../foundation/assets/svgs/LogoPoweredByBiologis';
import { ActiveFilterBar } from '../../foundation/components/active_filter_bar';
import FilterDropdown from '../../foundation/components/filter_dropdown/FilterDropdown';
import { useTableFilters } from '../../foundation/hooks';
import {
  useCreateVariantByMetaDataMutation,
  useGetVariantFilterOptionsQuery,
  useGetVariantsQuery,
} from '../api/variantCheckApi';
import { useRole } from '../authentication/hooks/useRole';
import Login from '../authentication/login/Login';
import { selectUser } from '../authentication/redux/selectors';
import { WORKFLOW_EDIT_ROLES } from '../variant_workflow/hooks/useVariantAccess';
import VariantCreateMetadataModal from './create_variant_metadata_modal/VariantCreateMetadataModal';
import { CreateVariantModal } from './create_variant_modal/CreateVariantModal';
import { useVariantCheckLogic } from './hooks/useVariantCheckLogic';
import { Variant, VariantListDataType } from './redux/types';

const RECALCULATING_ROW_TOOLTIP =
  'Variant is currently being recalculated and is not yet ready for interpretation.';
const CREATING_ROW_TOOLTIP =
  'Variant is currently being created and is not yet ready for interpretation.';

const VariantCheck = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoginModalVisible, setLoginModalVisibility] = useState(false);
  const [isCreateVariantModalVisible, setCreateVariantModalVisibility] =
    useState(false);
  const [isVariantMetaDataModalVisible, setVariantMetaDataModalVisibility] =
    useState(false);

  const [newVariantMetaData, setNewVariantMetaData] = useState<Variant | null>(
    null,
  );
  const [newVariantInfo, setNewVariantInfo] = useState<{
    transcript_primaryid: string;
    cdna: string;
  } | null>(null);

  const { data: filterOptionsData } = useGetVariantFilterOptionsQuery();

  const {
    columnFilters,
    dateFrom: createdDateFrom,
    dateTo: createdDateTo,
    setDateFrom: setCreatedDateFrom,
    setDateTo: setCreatedDateTo,
    handleFilterChange,
    handleFilterClear,
    handleClearAllFilters,
    getActiveFiltersCount,
    getDateBounds,
  } = useTableFilters({
    initialFilters: {
      interpretation_status: [] as string[],
      clinical_interpretation: [] as string[],
      creator: [] as string[],
      created_date: [] as string[],
    },
    dateFilterKey: 'created_date',
  });

  const createdDateBounds = getDateBounds(filterOptionsData?.created_date);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    queryArgs,
    hasRecalculatingVariant,
    hasRunningCreationTasks,
    trackedRefreshIsActive,
    activeRefreshVariantIds,
    trackedVariantId,
    fetchData,
    handleSearchValueChange,
    handleSearch,
    resetSearch,
  } = useVariantCheckLogic();

  // Merge query args with column filters — RTK Query refetches when any arg changes
  const variantsQueryArgs = useMemo(
    () => ({
      ...queryArgs,
      ...(columnFilters.interpretation_status.length && {
        interpretation_status_filter: columnFilters.interpretation_status,
      }),
      ...(columnFilters.clinical_interpretation.length && {
        clinical_interpretation_filter: columnFilters.clinical_interpretation,
      }),
      ...(columnFilters.creator.length && {
        creator_filter: columnFilters.creator,
      }),
      created_date_filter:
        columnFilters.created_date.length > 0
          ? columnFilters.created_date
          : undefined,
      created_date_from: createdDateFrom || undefined,
      created_date_to: createdDateTo || undefined,
    }),
    [queryArgs, columnFilters, createdDateFrom, createdDateTo],
  );

  const { currentData: variantsData, isFetching: isVariantsFetching } =
    useGetVariantsQuery(variantsQueryArgs, {
      // Always refetch when this page is (re)mounted via the left menu.
      // Without this, a still-cached list would be served as-is and variants
      // created in another browser tab ("creating" rows) would be missing.
      refetchOnMountOrArgChange: true,
      pollingInterval:
        hasRecalculatingVariant ||
        trackedRefreshIsActive ||
        activeRefreshVariantIds.size > 0 ||
        hasRunningCreationTasks
          ? 2000
          : 0,
    });

  const isVariantsPending = variantsData === undefined && isVariantsFetching;

  const { data: trackedVariantData } = useGetVariantsQuery(
    {
      limit: 1,
      offset: 0,
      variant_id: trackedVariantId,
    },
    { skip: trackedVariantId == null },
  );

  const variants = useMemo(() => {
    const listedVariants = variantsData?.list ?? [];
    const trackedRefreshVariant = trackedVariantData?.list[0];
    const variantsToDisplay =
      trackedRefreshVariant &&
      !listedVariants.some(
        (variant) => variant.variant_id === trackedRefreshVariant.variant_id,
      )
        ? [...listedVariants, trackedRefreshVariant]
        : listedVariants;

    return variantsToDisplay.map((variant) => ({
      ...variant,
      is_recalculating:
        variant.is_recalculating ||
        variant.variant_id === trackedVariantId ||
        activeRefreshVariantIds.has(variant.variant_id),
    }));
  }, [
    variantsData?.list,
    trackedVariantData?.list,
    trackedVariantId,
    activeRefreshVariantIds,
  ]);

  const totalVariantsCount = variantsData?.total ?? 0;
  const hasCreatingVariant = variants.some((variant) => variant.is_creating);

  // Search metrics are derived from the submitted query args instead of being
  // tracked as separate state alongside a manual fetch.
  const showSearchMetrics = {
    visible: Boolean(queryArgs.search),
    query: queryArgs.search,
  };

  // Mutation hook returns a [trigger, result] tuple
  const [createVariantByMetaData] = useCreateVariantByMetaDataMutation();

  const user = useSelector(selectUser);
  const { hasRole } = useRole();
  const canEditWorkflow = hasRole(WORKFLOW_EDIT_ROLES);

  const queryParams = useMemo(() => {
    return queryString.parse(location.search);
  }, [location.search]);

  useEffect(() => {
    if (queryParams && queryParams.search) {
      const v = queryParams.search as string;

      setSearch(v);
      navigate('', { state: location.state });
      setCurrentPage(1);
      fetchData(undefined, v);
    }
  }, [
    queryParams,
    setSearch,
    navigate,
    setCurrentPage,
    fetchData,
    location.state,
  ]);

  // Render every cell as a real <Link> (renders an <a href> under the hood).
  // This is what enables the browser's native "open in new tab" behaviour:
  //   - Ctrl/Cmd + click : opens the variant workflow in a new tab
  //   - Middle-click     : opens in a new tab
  //   - Right-click      : shows the standard "Open link in new tab" menu
  // We let the Link fill the whole cell (display: block) so the click target
  // stays as large as the previous row click, and inherit the cell color so
  // the table styling does not change visually.
  // While the variant is being recalculated (refresh job running) the link
  // is disabled, since the variant is read-only and not ready for
  // interpretation until the job finishes.
  const renderVariantLink = (
    value: React.ReactNode,
    record: VariantListDataType,
  ) => {
    if (record.is_recalculating || record.is_creating) {
      return <span className="l-variant-chk__cell--disabled">{value}</span>;
    }
    return (
      <Link
        to={`/variant-workflow/${record.variant_id}`}
        style={{ display: 'block', color: 'inherit' }}
      >
        {value}
      </Link>
    );
  };

  // Dedicated column (rendered last) carrying the processing badge, so
  // it's visible as its own indicator rather than crowding another column.
  const renderProcessingCell = (
    _value: React.ReactNode,
    record: VariantListDataType,
  ) => {
    if (record.is_creating) {
      return (
        <span className="l-variant-chk__creating-badge">
          <SyncOutlined spin /> creating
        </span>
      );
    }
    return record.is_recalculating ? (
      <span className="l-variant-chk__recalculating-badge">
        <SyncOutlined spin /> recalculating
      </span>
    ) : null;
  };

  const columns: ColumnsType<VariantListDataType> = useMemo(
    () => [
      {
        title: 'Gene',
        dataIndex: 'gene_hgnc_symbol',
        key: 'gene_hgnc_symbol',
        ellipsis: true,
        sorter: true,
        render: renderVariantLink,
      },
      {
        title: 'Transcript ID',
        dataIndex: 'transcript',
        key: 'transcript',
        ellipsis: true,
        sorter: true,
        render: renderVariantLink,
      },
      {
        title: 'cDNA Change',
        dataIndex: 'cdna',
        key: 'cdna',
        ellipsis: true,
        sorter: true,
        render: renderVariantLink,
      },
      {
        title: (
          <span>
            Interpretation status
            <FilterDropdown
              columnKey="interpretation_status"
              title="Interpretation Status"
              filterOptions={filterOptionsData?.interpretation_status || []}
              selectedValues={columnFilters.interpretation_status}
              onFilterChange={(values) =>
                handleFilterChange('interpretation_status', values)
              }
              onClear={() => handleFilterClear('interpretation_status')}
            />
          </span>
        ),
        dataIndex: 'interpretation_status',
        key: 'interpretation_status',
        ellipsis: true,
        sorter: true,
        render: renderVariantLink,
      },
      {
        title: (
          <span>
            Clinical interpretation
            <FilterDropdown
              columnKey="clinical_interpretation"
              title="Clinical Interpretation"
              filterOptions={filterOptionsData?.clinical_interpretation || []}
              selectedValues={columnFilters.clinical_interpretation}
              onFilterChange={(values) =>
                handleFilterChange('clinical_interpretation', values)
              }
              onClear={() => handleFilterClear('clinical_interpretation')}
            />
          </span>
        ),
        dataIndex: 'clinical_interpretation',
        key: 'clinical_interpretation',
        ellipsis: true,
        sorter: true,
        render: renderVariantLink,
      },
      {
        title: (
          <span>
            Creator
            <FilterDropdown
              columnKey="creator"
              title="Creator"
              filterOptions={filterOptionsData?.creator || []}
              selectedValues={columnFilters.creator}
              onFilterChange={(values) => handleFilterChange('creator', values)}
              onClear={() => handleFilterClear('creator')}
            />
          </span>
        ),
        dataIndex: 'creator',
        key: 'creator',
        ellipsis: true,
        sorter: true,
        render: renderVariantLink,
      },
      {
        title: (
          <span>
            Creation date
            <FilterDropdown
              columnKey="created_date"
              title="Creation Date"
              filterOptions={filterOptionsData?.created_date || []}
              selectedValues={columnFilters.created_date}
              onFilterChange={(values) =>
                handleFilterChange('created_date', values)
              }
              onClear={() => handleFilterClear('created_date')}
              isActive={Boolean(createdDateFrom || createdDateTo)}
              extraContent={
                <div className="filter-dropdown__date-range">
                  <div className="filter-dropdown__date-range-row">
                    <Input
                      type="date"
                      size="small"
                      value={createdDateFrom}
                      min={createdDateBounds.min}
                      max={createdDateBounds.max}
                      onChange={(e) => {
                        setCreatedDateFrom(e.target.value);
                      }}
                      className="filter-dropdown__date-input"
                    />
                    <span>-</span>
                    <Input
                      type="date"
                      size="small"
                      value={createdDateTo}
                      min={createdDateBounds.min}
                      max={createdDateBounds.max}
                      onChange={(e) => {
                        setCreatedDateTo(e.target.value);
                      }}
                      className="filter-dropdown__date-input"
                    />
                  </div>
                </div>
              }
            />
          </span>
        ),
        dataIndex: 'created_date',
        key: 'created_date',
        ellipsis: true,
        sorter: true,
        render: renderVariantLink,
      },
      {
        title: '',
        dataIndex: 'processing_status',
        key: 'processing_status',
        width: 160,
        render: renderProcessingCell,
      },
    ],
    [
      columnFilters,
      filterOptionsData,
      handleFilterChange,
      handleFilterClear,
      createdDateFrom,
      createdDateTo,
      createdDateBounds,
    ],
  );

  const handleLoginClick = () => {
    setLoginModalVisibility(true);
  };

  const onLoginModalClose = () => {
    setLoginModalVisibility(false);
  };

  const handleCreateVariantModalClose = () => {
    setCreateVariantModalVisibility(false);
  };

  const handleVariantCreationStarted = () => {
    resetSearch();
    navigate(location.pathname, { replace: true, state: null });
  };

  // Note: a former handleRowClick used to write the clicked variant into a
  // Redux slice and pre-fetch the classification arguments before calling
  // navigate(). It was removed because:
  //   1. The whole row is now made of <Link> cells (see renderVariantLink),
  //      which means the browser handles navigation natively and supports
  //      Ctrl/Cmd+click, middle-click and right-click "open in new tab".
  //   2. The target route /variant-workflow/:vid loads everything it needs
  //      from the URL param via the route loader / RTK Query cache, so the
  //      prefetch here was redundant and would not run anyway when opening
  //      in a new tab.
  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      SorterResult<VariantListDataType> | SorterResult<VariantListDataType>[],
  ) => {
    let sortField: string | undefined;
    let sortOrder: 'ascend' | 'descend' | undefined;

    if (Array.isArray(sorter)) {
      // If multi-sort, take the first sorter with a defined order
      const activeSorter = sorter.find((s) => s.order);
      if (activeSorter) {
        sortField = activeSorter.field as string;
        sortOrder = activeSorter.order as 'ascend' | 'descend';
      }
    } else if (sorter && sorter.field && sorter.order) {
      sortField = sorter.field as string;
      sortOrder = sorter.order as 'ascend' | 'descend';
    }

    // Switching the page size always returns to the first page, same as
    // the Probands/Samples table does.
    const nextPageSize = pagination.pageSize ?? pageSize;
    const nextPage =
      nextPageSize !== pageSize
        ? 1
        : pagination.current && pagination.current > 0
          ? pagination.current
          : 1;

    setPageSize(nextPageSize);
    setCurrentPage(nextPage);
    fetchData(
      (nextPage - 1) * nextPageSize,
      search,
      sortField,
      sortOrder,
      nextPageSize,
    );
  };

  const notFoundDescription = useMemo(() => {
    if (user) {
      return (
        <>
          <span className="l-variant-chk__search-results">
            Search term not found. Please add a new variant.
          </span>
          {canEditWorkflow && (
            <Button
              className="l-variant-chk__search-results-add-btn"
              type="primary"
              onClick={() => setCreateVariantModalVisibility(true)}
              icon={<FontAwesomeIcon icon={faPlus} />}
            >
              New Variant
            </Button>
          )}
        </>
      );
    } else {
      return (
        <span className="l-variant-chk__search-results">
          Search term not found. Please{' '}
          <a onClick={() => handleLoginClick()}>login</a> to add a new variant.
        </span>
      );
    }
  }, [user, canEditWorkflow]);

  const handleVariantCreate = async (
    variant_type: string,
  ): Promise<{
    variant_id: number;
    created: boolean;
    message: string;
  }> => {
    return new Promise(async (resolve, reject) => {
      try {
        if (newVariantInfo) {
          // The mutation trigger returns a promise with an `unwrap` method,
          const result = await createVariantByMetaData({
            transcript_primaryid: newVariantInfo?.transcript_primaryid,
            cdna: newVariantInfo?.cdna,
            variant_type,
          }).unwrap();

          resolve({
            variant_id: result.variant_id,
            created: result.created,
            message: result.message,
          });
        } else {
          reject(new Error('No variant info provided'));
        }
      } catch (error) {
        reject(error);
      }
    });
  };
  return (
    <div className="l-variant-chk">
      {isCreateVariantModalVisible && (
        <CreateVariantModal
          closeModal={handleCreateVariantModalClose}
          setNewVariantMetaData={setNewVariantMetaData}
          setNewVariantInfo={setNewVariantInfo}
          setVariantMetaDataModalVisibility={setVariantMetaDataModalVisibility}
          searchedString={search}
          onVariantCreationStarted={handleVariantCreationStarted}
        />
      )}
      {isVariantMetaDataModalVisible && (
        <VariantCreateMetadataModal
          closeModal={() => setVariantMetaDataModalVisibility(false)}
          variantMetaData={newVariantMetaData}
          handleVariantCreate={handleVariantCreate}
        />
      )}
      {isLoginModalVisible && <Login onClose={onLoginModalClose} />}
      <div className="l-variant-chk__search-wrapper">
        <Input
          prefix={<FontAwesomeIcon icon={faSearch} color={colors.primary} />}
          className="l-variant-chk__search-input"
          defaultValue=""
          placeholder="Enter variant or gene"
          value={search}
          onChange={handleSearchValueChange}
          onPressEnter={handleSearch}
          autoComplete="off"
        />
        <Button
          className="l-variant-chk__search-btn"
          type="primary"
          onClick={handleSearch}
        >
          Search
        </Button>
      </div>
      {showSearchMetrics.visible && (
        <div className="l-variant-chk__search-metrics">
          <span>
            {totalVariantsCount} results for:{' '}
            <span className="l-variant-chk__search-metrics-term">
              {showSearchMetrics?.query}
            </span>
          </span>

          {totalVariantsCount > 0 && user && canEditWorkflow && (
            <Button
              className="l-variant-chk__search-results-add-btn"
              type="primary"
              onClick={() => setCreateVariantModalVisibility(true)}
              icon={<FontAwesomeIcon icon={faPlus} />}
            >
              New Variant
            </Button>
          )}
        </div>
      )}

      <ActiveFilterBar
        columnFilters={columnFilters}
        dateFrom={createdDateFrom}
        dateTo={createdDateTo}
        dateLabel="Creation date"
        onFilterChange={handleFilterChange}
        onClearDateRange={() => {
          setCreatedDateFrom('');
          setCreatedDateTo('');
        }}
        onClearAll={handleClearAllFilters}
        getActiveFiltersCount={getActiveFiltersCount}
        classNamePrefix="l-variant-chk"
      />

      <div>
        <Table
          size="large"
          columns={columns}
          dataSource={variants}
          rowKey={(record) =>
            `${record.variant_id}-${record.transcript}-${record.cdna}`
          }
          loading={isVariantsPending}
          onChange={handleTableChange}
          // No onRow.onClick here on purpose: navigation is driven by the
          // <Link> rendered inside every cell (see renderVariantLink). This
          // gives us proper anchor semantics (open in new tab, hover URL,
          // accessibility) instead of a JS-only row click.
          // onRow only adds the "title" attribute (native tooltip) and
          // highlights the row while it is being recalculated.
          rowClassName={(record) => {
            if (record.is_creating) {
              return 'l-variant-chk__row--creating';
            }
            return record.is_recalculating
              ? 'l-variant-chk__row--recalculating'
              : '';
          }}
          onRow={(record) => {
            if (record.is_creating) {
              return { title: CREATING_ROW_TOOLTIP };
            }
            return record.is_recalculating
              ? { title: RECALCULATING_ROW_TOOLTIP }
              : {};
          }}
          pagination={{
            current: currentPage,
            total: totalVariantsCount,
            pageSize,
            showSizeChanger: true,
          }}
          locale={{
            emptyText: isVariantsPending ? (
              <span />
            ) : (
              <Empty description={notFoundDescription}></Empty>
            ),
          }}
        />
        {hasRecalculatingVariant && (
          <div className="l-variant-chk__recalculating-hint">
            Variants marked{' '}
            <span className="l-variant-chk__recalculating-badge">
              <SyncOutlined spin /> recalculating
            </span>{' '}
            are still being processed by the pipeline, and are not yet available
            for interpretation.
          </div>
        )}
        {hasCreatingVariant && (
          <div className="l-variant-chk__creating-hint">
            Variants marked{' '}
            <span className="l-variant-chk__creating-badge">
              <SyncOutlined spin /> creating
            </span>{' '}
            are still being processed by the pipeline, and are not yet available
            for interpretation.
          </div>
        )}
      </div>
      <div className="l-variant-chk__powered-by">
        <LogoPoweredByBiologis />
      </div>
    </div>
  );
};

export default VariantCheck;
